import React, { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Truck, Users, Wallet, ArrowLeft, FileCheck2 } from "lucide-react";
import { inr } from "@/components/billing/BillingParts";
import { Suppliers, PurchaseBills, Expenses } from "@/components/purchases/PurchaseParts";
import GstReco from "@/components/purchases/GstReco";
import Gstr3b from "@/components/purchases/Gstr3b";
import { BusinessSwitcher } from "@/components/biz/BusinessSwitcher";
import { NotificationBell } from "@/components/portal/NotificationBell";

const TABS = [["bills", "Purchase bills", Truck], ["suppliers", "Suppliers", Users], ["expenses", "Expenses", Wallet], ["gst", "GST reco (2B)", FileCheck2], ["gstr3b", "GSTR-3B draft", FileCheck2]];

export function PurchasesPanel({ clientId }) {
  const qs = clientId ? `?client_id=${clientId}` : "";
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "bills";
  const [summary, setSummary] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const load = () => { api.get(`/purchases/summary${qs}`).then(r => setSummary(r.data)); api.get(`/purchases/suppliers${qs}`).then(r => setSuppliers(r.data)); };
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line
  const setTab = (k) => setParams(p => { const n = new URLSearchParams(p); n.set("tab", k); return n; });
  return (
    <div className="space-y-6" data-testid="purchases-panel">
      {summary ? <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pur-kpis">
        {[["Purchased (all)", inr(summary.purchased), "kpi-purchased"], ["Payable to suppliers", inr(summary.payable), "kpi-payable"], [`Input GST FY ${summary.fy}`, inr(summary.fy_input_gst), "kpi-input-gst"], [`Expenses FY ${summary.fy}`, inr(summary.fy_expenses), "kpi-expenses"]].map(([l, v, t]) => (
          <div key={t} data-testid={t} className="bg-white border border-zinc-200 p-4"><div className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold">{l}</div><div className="font-heading font-bold text-lg tabular mt-1">{v}</div></div>))}
      </div> : null}
      <nav className="flex gap-1 border-b border-zinc-200 overflow-x-auto" data-testid="pur-tabs">{TABS.map(([k, l, Icon]) => <button key={k} data-testid={`pur-tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold px-3 py-2.5 border-b-2 transition-colors ${tab === k ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}><Icon size={14} /> {l}</button>)}</nav>
      {tab === "bills" ? <PurchaseBills qs={qs} suppliers={suppliers} onChanged={load} /> : null}
      {tab === "suppliers" ? <Suppliers qs={qs} suppliers={suppliers} onChanged={load} /> : null}
      {tab === "expenses" ? <Expenses qs={qs} onChanged={load} /> : null}
      {tab === "gst" ? <GstReco /> : null}
      {tab === "gstr3b" ? <Gstr3b /> : null}
    </div>
  );
}

export function ClientHeader({ kicker, title, links }) {
  const { logout, user } = useAuth();
  return (
    <header className="bg-white border-b border-zinc-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/my-business" data-testid="client-back" className="p-2 border border-zinc-200 hover:bg-zinc-900 hover:text-white transition-colors"><ArrowLeft size={14} /></Link>
          <div className="min-w-0"><div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">{kicker}</div><div className="font-heading font-bold tracking-tight truncate">{title}</div></div>
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto no-scrollbar [&>*]:shrink-0 [&>a]:whitespace-nowrap" data-testid="client-nav">
          <BusinessSwitcher />
          {!user?.staff_role ? <NotificationBell /> : null}
          {links.map(([to, l, tid]) => <Link key={to} to={to} data-testid={tid} className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors">{l}</Link>)}
          <button data-testid="client-signout" onClick={logout} className="text-[10px] tracking-[0.14em] uppercase font-semibold px-3 py-1.5 border border-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors">Sign out</button>
        </div>
      </div>
    </header>
  );
}

export const CLIENT_LINKS = [["/my-business", "Home", "nav-home"], ["/my-billing", "Billing", "nav-billing"], ["/pos", "POS", "nav-pos"], ["/my-inventory", "Inventory", "nav-inventory"], ["/my-purchases", "Purchases", "nav-purchases"], ["/my-reports", "Reports", "nav-reports"], ["/my-khata", "Khata", "nav-khata"], ["/my-portal", "Tax portal", "nav-portal"]];

export default function MyPurchases() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-zinc-500 tracking-[0.2em] uppercase text-xs">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "client") return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen bg-zinc-50" data-testid="my-purchases">
      <ClientHeader kicker="Purchases & Expenses" title={user.name} links={CLIENT_LINKS.filter(l => l[0] !== "/my-purchases")} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6"><PurchasesPanel clientId={null} /></main>
    </div>
  );
}

export function ClientPurchases() {
  const { id } = useParams();
  return (
    <div className="space-y-6" data-testid="client-purchases-page">
      <header><Link to="/clients" className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 hover:text-zinc-900 flex items-center gap-1"><ArrowLeft size={12} /> Clients</Link><h1 className="font-heading font-black tracking-tight text-3xl sm:text-4xl mt-2">Purchases & Expenses</h1></header>
      <PurchasesPanel clientId={id} />
    </div>
  );
}
